import {
  type FieldApi,
  FieldRow,
  handleNumberChange,
  numberInputValue,
} from '@/components/form/formHelpers'
import type { LicenseResponseBody } from '@/features/Licenses/api/licenseHooks'
import { useUpdateOrgLicenseEntitlements } from '@/features/Licenses/api/licenseHooks'
import { LicenseFormDialog } from '@/features/Licenses/components/LicenseFormDialog'
import {
  buildMutationOptions,
  mapMutationError,
} from '@/features/Licenses/lib/mutationHandlers'
import { Button, Input } from '@chakra-ui/react'
import { useForm } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

type Props = {
  license: LicenseResponseBody
  onSuccess: () => void
}

const INLINE_STATUSES = [400, 409, 502]

export function EditEntitlementsForm({ license, onSuccess }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()
  const retryRef = useRef<() => void>(() => {
    // replaced after form initialization
  })

  const mutation = useUpdateOrgLicenseEntitlements(
    buildMutationOptions({
      queryClient,
      license,
      onSuccess,
      toast: {
        id: 'update-entitlements-error',
        title: String(t('licenses.actions.editEntitlements.error.title')),
        description: String(
          t('licenses.actions.editEntitlements.error.generic'),
        ),
        retry: {
          label: String(t('licenses.actions.editEntitlements.error.retry')),
          onClick: () => retryRef.current(),
        },
      },
      inlineStatuses: INLINE_STATUSES,
    }),
  )

  const schema = z.object({
    maxConnections: z.coerce
      .number()
      .int()
      .min(
        1,
        String(
          t('licenses.actions.editEntitlements.validation.positiveNumber'),
        ),
      ),
    maxMessagesPerSecond: z.coerce
      .number()
      .int()
      .min(
        1,
        String(
          t('licenses.actions.editEntitlements.validation.positiveNumber'),
        ),
      ),
  })

  const form = useForm({
    defaultValues: {
      maxConnections: license.entitlements.maxConnections,
      maxMessagesPerSecond: license.entitlements.maxMessagesPerSecond,
    },
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      await mutation
        .mutateAsync({
          pathParams: { orgId: license.orgId, licenseId: license.id },
          body: {
            maxConnections: Number(value.maxConnections),
            maxMessagesPerSecond: Number(value.maxMessagesPerSecond),
          },
        })
        .catch(() => {
          // handled by onError
        })
    },
  })

  retryRef.current = () => void form.handleSubmit()

  const inDialogError = mapMutationError(mutation, {
    inlineStatuses: INLINE_STATUSES,
    correlationIdStatuses: [502],
    fallbackDetail: String(
      t('licenses.actions.editEntitlements.error.generic'),
    ),
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <LicenseFormDialog
        title={String(t('licenses.actions.editEntitlements.title'))}
        errorTitle={String(t('licenses.actions.editEntitlements.error.title'))}
        correlationIdLabel={String(
          t('licenses.actions.editEntitlements.error.correlationId'),
        )}
        inDialogError={inDialogError}
        cancelLabel={String(t('licenses.actions.editEntitlements.cancel'))}
        submitButton={
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                colorPalette="brand"
                loading={Boolean(isSubmitting) || mutation.isPending}
                disabled={!canSubmit || mutation.isPending}
              >
                {String(t('licenses.actions.editEntitlements.submit'))}
              </Button>
            )}
          </form.Subscribe>
        }
      >
        <form.Field name="maxConnections">
          {(field) => (
            <FieldRow
              field={field as FieldApi<unknown>}
              label={String(
                t(
                  'licenses.actions.editEntitlements.fields.maxConnections.label',
                ),
              )}
            >
              <Input
                type="number"
                min={1}
                value={numberInputValue(field.state.value)}
                onChange={handleNumberChange(field)}
                onBlur={field.handleBlur}
              />
            </FieldRow>
          )}
        </form.Field>
        <form.Field name="maxMessagesPerSecond">
          {(field) => (
            <FieldRow
              field={field as FieldApi<unknown>}
              label={String(
                t(
                  'licenses.actions.editEntitlements.fields.maxMessagesPerSecond.label',
                ),
              )}
            >
              <Input
                type="number"
                min={1}
                value={numberInputValue(field.state.value)}
                onChange={handleNumberChange(field)}
                onBlur={field.handleBlur}
              />
            </FieldRow>
          )}
        </form.Field>
      </LicenseFormDialog>
    </form>
  )
}
